import { type IDocument, type LawdocsWillDocument } from "@types";
import { applicationUrl } from "@constants";
import { productTypes } from "@shared/constants/productTypes";
import { purchaseTypes } from "@shared/constants/purchaseTypes";

export function getLawdocsRedirectUrl(document: IDocument): string {
  let url = "";

  if (document.canBeEdited && !document.isCorrected) {
    url = `${applicationUrl}/my-documents/edit/${document.id}`;
  } else {
    url = `${applicationUrl}/opret-dokument/${document.type}`;
    if (document.identifier == null) {
      url +=
        document.purchasedAsType == null
          ? `?purchaseType=${purchaseTypes.digitalDocumentBasis}`
          : `?purchaseType=${document.purchasedAsType}`;
    } else {
      url += `?id=${document.identifier}`;
    }
  }

  return url;
}

// Product types whose party names live on the matched lawdocs document rather
// than on the document record returned by the documents API.
const lawdocsOwnerNameTypes = new Set<IDocument["type"]>([
  productTypes.will,
  productTypes.prenup,
  productTypes.coop,
  productTypes.futurePow,
]);

export const hasLawdocsOwnerNames = (type: IDocument["type"]): boolean => lawdocsOwnerNameTypes.has(type);

export const getPrimaryReceiverName = (document: LawdocsWillDocument): string | undefined => {
  for (const [key, value] of Object.entries(document)) {
    const match = key.match(/^receivers\[(\d+)]__receiver_type$/);
    if (match && value === "primary") {
      const index = match[1];
      const nameKey = `receivers[${index}]__name`;
      //@ts-expect-error LawDocs return data in a wierd way
      return document[nameKey];
    }
  }

  return;
};

export interface DocumentOwnerNames {
  primaryOwnerName?: string;
  secondaryOwnerName?: string;
}

// The two parties on a document live under different lawdocs keys per product
// type. Kept in one place so the cart and the documents overview stay in sync.
export const resolveDocumentOwnerNames = (
  type: IDocument["type"],
  lawDocument: LawdocsWillDocument,
): DocumentOwnerNames => {
  switch (type) {
    case productTypes.prenup: {
      return { primaryOwnerName: lawDocument.person1_name, secondaryOwnerName: lawDocument.person2_name };
    }
    case productTypes.will: {
      return {
        primaryOwnerName: lawDocument.field_testator__field_fullname,
        secondaryOwnerName: lawDocument.field_testator_spouse__field_fullname,
      };
    }
    case productTypes.coop: {
      return { primaryOwnerName: lawDocument.person_1_name, secondaryOwnerName: lawDocument.person_2_name };
    }
    case productTypes.futurePow: {
      return { primaryOwnerName: lawDocument.person_1_name, secondaryOwnerName: getPrimaryReceiverName(lawDocument) };
    }
    default: {
      return {};
    }
  }
};
